
import { useState, useEffect } from 'react';

interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  description: string;
  category: string;
  isNew?: boolean;
  isOnSale?: boolean;
}

interface ProductModalProps {
  product: Product;
  onClose: () => void;
  onAddToCart: (productId: string, quantity: number, productName: string, productPrice: number, productImage: string) => void;
}

export default function ProductModal({ product, onClose, onAddToCart }: ProductModalProps) {
  const [quantity, setQuantity] = useState(1);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [sweepDirection, setSweepDirection] = useState('forward');

  useEffect(() => {
    // Entrada con animación ultra suave y barrido dorado
    const timer = setTimeout(() => {
      setIsVisible(true);
      setSweepDirection('forward');
    }, 50);
    
    // Prevenir scroll del body
    document.body.style.overflow = 'hidden';
    
    return () => {
      clearTimeout(timer);
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    setIsVisible(false);
    setSweepDirection('reverse');
    // Esperar a que termine la animación ultra suave antes de cerrar
    setTimeout(() => {
      onClose();
    }, 800);
  };

  const handleAddToCart = () => {
    onAddToCart(product.id, quantity, product.name, product.price, product.image);
    handleClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-800 ease-in-out ${
        isVisible && !isClosing 
          ? 'bg-black/90 backdrop-blur-md' 
          : 'bg-black/0 backdrop-blur-none'
      }`}
      onClick={handleBackdropClick}
    >
      {/* Partículas doradas ultra animadas con entrada suave */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-3 h-3 bg-[#C6A45C] rounded-full transition-all duration-1000 ease-out ${
              isVisible && !isClosing ? 'opacity-60 scale-100 animate-ping' : 'opacity-0 scale-0'
            }`}
            style={{
              left: `${10 + i * 8}%`,
              top: `${20 + i * 6}%`,
              animationDelay: `${i * 0.2}s`,
              animationDuration: '3s',
              transitionDelay: isVisible ? `${i * 80}ms` : '0ms'
            }}
          />
        ))}
        {/* Partículas adicionales en movimiento */}
        {[...Array(8)].map((_, i) => (
          <div
            key={`extra-${i}`}
            className={`absolute w-2 h-2 bg-gradient-to-r from-[#C6A45C] to-yellow-400 rounded-full transition-all duration-1200 ease-out ${
              isVisible && !isClosing ? 'opacity-40 scale-100 animate-pulse' : 'opacity-0 scale-0'
            }`}
            style={{
              right: `${15 + i * 10}%`,
              bottom: `${25 + i * 7}%`,
              animationDelay: `${i * 0.4}s`,
              animationDuration: '4s',
              transitionDelay: isVisible ? `${i * 120}ms` : '0ms'
            }}
          />
        ))}
      </div>

      {/* Modal Container con animaciones ultra suaves y efectos dorados */}
      <div 
        className={`relative bg-black border-2 rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden transition-all duration-800 ease-out transform ${
          isVisible && !isClosing 
            ? 'scale-100 opacity-100 translate-y-0 rotate-0' 
            : 'scale-85 opacity-0 translate-y-16 rotate-3'
        }`}
        style={{
          borderColor: isVisible && !isClosing ? '#C6A45C' : 'transparent',
          boxShadow: isVisible && !isClosing 
            ? '0 30px 60px -12px rgba(198, 164, 92, 0.4), 0 0 0 1px rgba(198, 164, 92, 0.2), inset 0 1px 0 rgba(198, 164, 92, 0.1)' 
            : '0 0 0 0px rgba(198, 164, 92, 0)',
          transitionProperty: 'all, box-shadow, border-color',
          transitionTimingFunction: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        }}
      >
        {/* Efecto de contorno dorado parpadeante ultra elegante */}
        <div className={`absolute inset-0 rounded-xl pointer-events-none transition-all duration-1000 ease-out ${
          isVisible && !isClosing ? 'opacity-100' : 'opacity-0'
        }`}>
          {/* Contorno superior */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C6A45C] to-transparent animate-pulse"></div>
          {/* Contorno inferior */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C6A45C] to-transparent animate-pulse" style={{ animationDelay: '0.5s' }}></div>
          {/* Contorno izquierdo */}
          <div className="absolute top-0 bottom-0 left-0 w-px bg-gradient-to-b from-transparent via-[#C6A45C] to-transparent animate-pulse" style={{ animationDelay: '0.25s' }}></div>
          {/* Contorno derecho */}
          <div className="absolute top-0 bottom-0 right-0 w-px bg-gradient-to-b from-transparent via-[#C6A45C] to-transparent animate-pulse" style={{ animationDelay: '0.75s' }}></div>
        </div>

        {/* Efecto de barrido dorado ultra espectacular */}
        <div className={`absolute inset-0 rounded-xl overflow-hidden pointer-events-none transition-all duration-800 ease-out ${
          isVisible && !isClosing ? 'opacity-100' : 'opacity-0'
        }`}>
          <div 
            className={`absolute inset-0 bg-gradient-to-r from-transparent via-[#C6A45C]/30 to-transparent transition-transform duration-1000 ease-out ${
              sweepDirection === 'forward' && isVisible && !isClosing
                ? 'transform translate-x-0'
                : sweepDirection === 'reverse' && isClosing
                ? 'transform -translate-x-full'
                : 'transform -translate-x-full'
            }`}
            style={{
              width: '200%',
              left: '-100%',
              transitionDelay: isVisible && !isClosing ? '200ms' : '0ms'
            }}
          ></div>
          {/* Segundo barrido más sutil */}
          <div 
            className={`absolute inset-0 bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent transition-transform duration-1200 ease-out ${
              sweepDirection === 'forward' && isVisible && !isClosing
                ? 'transform translate-x-0'
                : sweepDirection === 'reverse' && isClosing
                ? 'transform -translate-x-full'
                : 'transform -translate-x-full'
            }`}
            style={{
              width: '150%',
              left: '-75%',
              transitionDelay: isVisible && !isClosing ? '400ms' : '100ms'
            }}
          ></div>
        </div>

        {/* Efecto shimmer en el borde ultra suave y continuo */}
        <div className={`absolute inset-0 rounded-xl transition-all duration-1000 ease-out pointer-events-none ${
          isVisible && !isClosing ? 'opacity-100' : 'opacity-0'
        }`}>
          <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-[#C6A45C]/10 to-transparent animate-pulse"></div>
          <div className="absolute inset-0 rounded-xl bg-gradient-to-l from-transparent via-yellow-400/10 to-transparent animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        {/* Botón de cerrar con animación ultra elegante */}
        <button
          onClick={handleClose}
          className={`absolute top-6 right-6 z-10 w-12 h-12 bg-black/95 backdrop-blur-md border-2 border-[#C6A45C] rounded-full flex items-center justify-center text-white hover:text-[#C6A45C] hover:bg-[#C6A45C]/10 transition-all duration-600 ease-out transform hover:scale-110 hover:rotate-90 ${
            isVisible && !isClosing 
              ? 'opacity-100 translate-x-0 scale-100' 
              : 'opacity-0 translate-x-8 scale-75'
          }`}
          style={{ 
            transitionDelay: isVisible && !isClosing ? '400ms' : '0ms',
            transitionProperty: 'all, transform, opacity',
            boxShadow: '0 0 20px rgba(198, 164, 92, 0.3)'
          }}
        >
          <i className="ri-close-line text-xl transition-transform duration-400"></i>
        </button>

        <div className="flex flex-col lg:flex-row h-full">
          {/* Imagen del producto optimizada sin espacio extra */}
          <div className={`lg:w-3/5 relative overflow-hidden transition-all duration-900 ease-out ${
            isVisible && !isClosing 
              ? 'opacity-100 translate-x-0' 
              : 'opacity-0 -translate-x-16'
          }`}
          style={{ transitionDelay: isVisible && !isClosing ? '200ms' : '0ms' }}
          >
            <div className="h-full relative group flex items-center justify-center">
              <img
                src={product.image}
                alt={product.name}
                className="max-w-full max-h-full object-contain transition-transform duration-1200 ease-out group-hover:scale-105"
                style={{ maxHeight: '85vh' }}
              />
              
              {/* Overlay con gradiente dorado suave */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-800 ease-out"></div>
              
              {/* Efecto de brillo dorado en la imagen */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#C6A45C]/0 via-[#C6A45C]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000 ease-out"></div>
              
              {/* Badges con animación de entrada ultra suave */}
              {product.isNew && (
                <div className={`absolute top-6 left-6 bg-gradient-to-r from-[#C6A45C] to-yellow-500 text-black px-4 py-2 text-sm font-thin uppercase tracking-wider rounded-full shadow-lg transition-all duration-700 ease-out ${
                  isVisible && !isClosing 
                    ? 'opacity-100 translate-y-0 animate-bounce' 
                    : 'opacity-0 -translate-y-6'
                }`}
                style={{ 
                  transitionDelay: isVisible && !isClosing ? '500ms' : '0ms',
                  fontFamily: '"Lato", sans-serif',
                  boxShadow: '0 4px 15px rgba(198, 164, 92, 0.4)'
                }}
                >
                  NUEVO
                </div>
              )}
              {product.isOnSale && (
                <div className={`absolute top-6 right-6 bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-2 text-sm font-thin uppercase tracking-wider rounded-full shadow-lg transition-all duration-700 ease-out ${
                  isVisible && !isClosing 
                    ? 'opacity-100 translate-y-0 animate-pulse' 
                    : 'opacity-0 -translate-y-6'
                }`}
                style={{ 
                  transitionDelay: isVisible && !isClosing ? '600ms' : '0ms',
                  fontFamily: '"Lato", sans-serif',
                  boxShadow: '0 4px 15px rgba(220, 38, 38, 0.4)'
                }}
                >
                  OFERTA
                </div>
              )}
            </div>
          </div>

          {/* Detalles del producto optimizados */}
          <div className={`lg:w-2/5 p-8 flex flex-col justify-center transition-all duration-900 ease-out ${
            isVisible && !isClosing 
              ? 'opacity-100 translate-x-0' 
              : 'opacity-0 translate-x-16'
          }`} 
          style={{ transitionDelay: isVisible && !isClosing ? '300ms' : '0ms' }}
          >
            <div className="space-y-6">
              {/* Categoría con animación suave y brillo dorado */}
              <div className={`text-[#C6A45C] text-base uppercase tracking-wider font-thin transition-all duration-700 ease-out ${
                isVisible && !isClosing 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-6'
              }`}
              style={{ 
                transitionDelay: isVisible && !isClosing ? '450ms' : '0ms',
                fontFamily: '"Lato", sans-serif',
                textShadow: '0 0 10px rgba(198, 164, 92, 0.3)'
              }}
              >
                {product.category}
              </div>

              {/* Nombre del producto con efecto dorado */}
              <h2 className={`text-3xl lg:text-4xl font-thin text-gray-200 uppercase tracking-wider transition-all duration-800 ease-out ${
                isVisible && !isClosing 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-8'
              }`}
              style={{ 
                fontFamily: '"Lato", sans-serif',
                transitionDelay: isVisible && !isClosing ? '500ms' : '0ms',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
              }}
              >
                {product.name}
              </h2>

              {/* Precio con animación elegante y brillo dorado */}
              <div className={`flex items-center gap-4 transition-all duration-800 ease-out ${
                isVisible && !isClosing 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: isVisible && !isClosing ? '550ms' : '0ms' }}
              >
                <span className="text-3xl font-thin text-[#C6A45C]" style={{ 
                  fontFamily: '"Lato", sans-serif',
                  textShadow: '0 0 15px rgba(198, 164, 92, 0.4)'
                }}>
                  ${product.price}
                </span>
                {product.originalPrice && (
                  <span className="text-xl text-gray-400 line-through font-thin" style={{ fontFamily: '"Lato", sans-serif' }}>
                    ${product.originalPrice}
                  </span>
                )}
              </div>

              {/* Descripción con mejor tipografía */}
              <p className={`text-gray-300 leading-relaxed font-thin text-lg transition-all duration-800 ease-out ${
                isVisible && !isClosing 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-8'
              }`}
              style={{ 
                transitionDelay: isVisible && !isClosing ? '600ms' : '0ms',
                fontFamily: '"Lato", sans-serif'
              }}
              >
                {product.description}
              </p>

              {/* Información adicional con entrada escalonada y iconos dorados */}
              <div className={`space-y-3 transition-all duration-800 ease-out ${
                isVisible && !isClosing 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: isVisible && !isClosing ? '650ms' : '0ms' }}
              >
                <div className="flex items-center gap-4 text-base text-gray-300 font-thin" style={{ fontFamily: '"Lato", sans-serif' }}>
                  <i className="ri-truck-line text-[#C6A45C] text-xl" style={{ textShadow: '0 0 8px rgba(198, 164, 92, 0.5)' }}></i>
                  <span>ENVÍO GRATIS EN COMPRAS SUPERIORES A USD 300</span>
                </div>
                <div className="flex items-center gap-4 text-base text-gray-300 font-thin" style={{ fontFamily: '"Lato", sans-serif' }}>
                  <i className="ri-shield-check-line text-[#C6A45C] text-xl" style={{ textShadow: '0 0 8px rgba(198, 164, 92, 0.5)' }}></i>
                  <span>GARANTÍA DE CALIDAD PREMIUM</span>
                </div>
                <div className="flex items-center gap-4 text-base text-gray-300 font-thin" style={{ fontFamily: '"Lato", sans-serif' }}>
                  <i className="ri-secure-payment-line text-[#C6A45C] text-xl" style={{ textShadow: '0 0 8px rgba(198, 164, 92, 0.5)' }}></i>
                  <span>PAGO SEGURO Y PROTEGIDO</span>
                </div>
              </div>

              {/* Controles de cantidad y botón con entrada final ultra elegante */}
              <div className={`space-y-6 transition-all duration-900 ease-out ${
                isVisible && !isClosing 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: isVisible && !isClosing ? '700ms' : '0ms' }}
              >
                {/* Selector de cantidad con diseño premium y número dorado */}
                <div className="flex items-center gap-6">
                  <span className="text-gray-200 font-thin text-lg uppercase tracking-wider" style={{ fontFamily: '"Lato", sans-serif' }}>CANTIDAD:</span>
                  <div className="flex items-center border-2 border-[#C6A45C] rounded-xl overflow-hidden bg-gray-900/50 backdrop-blur-sm">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-14 h-14 bg-[#C6A45C] hover:bg-yellow-500 text-black flex items-center justify-center transition-all duration-500 ease-out transform hover:scale-110 font-thin text-xl shadow-lg"
                      style={{ boxShadow: '0 4px 15px rgba(198, 164, 92, 0.3)', fontFamily: '"Lato", sans-serif' }}
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="w-24 h-14 bg-gray-800/80 text-[#C6A45C] flex items-center justify-center font-thin text-xl border-x-2 border-[#C6A45C]" style={{ 
                      fontFamily: '"Lato", sans-serif',
                      textShadow: '0 0 10px rgba(198, 164, 92, 0.5)'
                    }}>
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-14 h-14 bg-[#C6A45C] hover:bg-yellow-500 text-black flex items-center justify-center transition-all duration-500 ease-out transform hover:scale-110 font-thin text-xl shadow-lg"
                      style={{ boxShadow: '0 4px 15px rgba(198, 164, 92, 0.3)', fontFamily: '"Lato", sans-serif' }}
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                </div>

                {/* Botón agregar al carrito con efectos ultra suaves y dorados */}
                <button
                  onClick={handleAddToCart}
                  className="w-full bg-gradient-to-r from-[#C6A45C] to-yellow-500 hover:from-yellow-500 hover:to-[#C6A45C] text-black font-thin py-5 px-8 text-lg uppercase tracking-wider transition-all duration-600 ease-out transform hover:scale-105 relative overflow-hidden group rounded-xl"
                  style={{ 
                    fontFamily: '"Lato", sans-serif',
                    boxShadow: '0 8px 25px rgba(198, 164, 92, 0.4)'
                  }}
                >
                  <span className="relative z-10 flex items-center justify-center gap-3">
                    <i className="ri-shopping-cart-line text-xl"></i>
                    AGREGAR AL CARRITO
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-1200 ease-out"></div>
                  <div className="absolute inset-0 bg-gradient-to-l from-transparent via-yellow-300/10 to-transparent transform translate-x-full group-hover:-translate-x-full transition-transform duration-1000 ease-out"></div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
